/**
 * Client-side customization layer for PUBLISHED dashboards.
 *
 * The dashboard structure (layout_json) arrives from the backend and is not
 * editable there. This layer lets the frontend append/override configuration
 * on top of what the backend returns, at render time:
 *
 *  - `design`    — dashboard-level look (page background, card style, spacing)
 *                  applied as scoped `--dvm-design-*` CSS variables on the
 *                  published view host. Falls back to today's hardcoded values
 *                  when absent, so existing dashboards render unchanged.
 *  - `overrides` — surgical per-widget patches deep-merged onto each widget
 *                  before render (`byType` first, then `byWidgetId` wins).
 *                  A patch can touch anything the widget carries — most
 *                  usefully `capabilities` (charts rebuild their option from
 *                  capabilities on input change) and `format`.
 *
 * Both keys can come from two places, merged in this order (later wins):
 *   1. `layout_json.design` / `layout_json.overrides` (backend-persisted)
 *   2. an external asset file `assets/dashboard-config/overrides/<slug>.json`
 *      (pure frontend — customize any published dashboard with no backend
 *      change and no re-publish)
 *
 * Merge semantics everywhere in this file: plain objects deep-merge, arrays
 * and scalars REPLACE (never concat — concat is how the builder config merge
 * works and it duplicates entries when applied to widget data).
 */

export interface DashboardDesign {
  page?: {
    /** Any CSS background value (color, gradient, image). */
    bg?: string;
    /** Convenience alternative to `bg`: 2+ colors → linear-gradient. */
    gradient?: string[];
    /** Gradient direction, e.g. "180deg" (default) or "to right". */
    gradientDirection?: string;
    /** Canvas grid gap in px (view mode). */
    gap?: number;
    /** Canvas padding in px. */
    padding?: number;
  };
  card?: {
    bg?: string;
    borderColor?: string;
    borderWidth?: number;
    radius?: number;
    /** 'none' | 'soft' | 'strong' — or any raw CSS box-shadow string. */
    shadow?: string;
    headerBg?: string;
    headerColor?: string;
    /** When true, the design's header colors also override tiles that set
     *  their OWN header colors in the per-tile Format popup (default: the
     *  per-tile colors win). Only meaningful with headerBg/headerColor set. */
    headerForce?: boolean;
  };
  /** KPI / stat-card design for this dashboard (overrides the theme's
   *  statCard tokens, scoped to the published host). */
  kpi?: {
    /** Accent bar colour on the card's left edge. */
    accentColor?: string;
    /** Accent bar thickness in px (0 hides it). */
    accentWidth?: number;
    /** `inline` (label left, value right) | `stacked` (label above value). */
    layout?: 'inline' | 'stacked';
  };
  /** Tinted panel drawn behind filter / dropdown controls. */
  filter?: {
    panelBg?: string;
    panelBorderColor?: string;
  };
  /** "Available Views" rail (the viewer's other published dashboards, on the
   *  right). `enabled` overrides the project-wide
   *  PUBLISHED_VIEWS_RAIL_ENABLED environment flag for THIS dashboard. */
  rail?: {
    enabled?: boolean;
  };
}

/** A patch applied onto a widget node (deep-merged; arrays/scalars replace). */
export type WidgetPatch = Record<string, unknown>;

export interface DashboardOverrides {
  /** Keyed by widget `type` (e.g. "bar-column", "kpi-card"), case-insensitive. */
  byType?: Record<string, WidgetPatch>;
  /** Keyed by widget id (`widgetId`/`id` from layout_json) or componentId. */
  byWidgetId?: Record<string, WidgetPatch>;
}

export interface DashboardCustomization {
  design?: DashboardDesign;
  overrides?: DashboardOverrides;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/** Deep-merge `patch` over `base`. Objects merge recursively; arrays and
 *  scalars replace. Neither input is mutated. */
export function deepMergePatch<T extends Record<string, unknown>>(base: T, patch: Record<string, unknown>): T {
  const out: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(patch)) {
    if (key.startsWith('//')) continue; // comment keys, same idiom as builder configs
    const existing = out[key];
    if (isPlainObject(value) && isPlainObject(existing)) {
      out[key] = deepMergePatch(existing, value);
    } else {
      out[key] = value;
    }
  }
  return out as T;
}

/** The ONE layout_json key this feature owns. Namespaced deliberately: generic
 *  top-level keys like `overrides` already exist in real published dashboards
 *  with unrelated meanings (e.g. dropdown/filter definitions), so reading or
 *  deleting them would corrupt backend-owned data. Everything we persist lives
 *  under this key and nothing outside it is ever touched. */
export const CUSTOMIZATION_LAYOUT_KEY = 'dvmCustomization';

/** Combine backend-persisted customization (from layout_json's
 *  `dvmCustomization` key) with the external asset-file customization.
 *  External wins. Returns null when neither side has anything, so callers can
 *  skip the whole layer cheaply. */
export function resolveDashboardCustomization(
  layoutJson: Record<string, unknown> | null | undefined,
  external: DashboardCustomization | null | undefined,
): DashboardCustomization | null {
  const fromLayout: DashboardCustomization = {};
  const stored = layoutJson?.[CUSTOMIZATION_LAYOUT_KEY];
  if (isPlainObject(stored)) {
    if (isPlainObject(stored['design'])) fromLayout.design = stored['design'] as DashboardDesign;
    if (isPlainObject(stored['overrides'])) fromLayout.overrides = stored['overrides'] as DashboardOverrides;
  }

  const hasLayout = !!(fromLayout.design || fromLayout.overrides);
  const hasExternal = !!(external && (external.design || external.overrides));
  if (!hasLayout && !hasExternal) return null;
  if (!hasExternal) return fromLayout;
  if (!hasLayout) return external!;
  return deepMergePatch(fromLayout as Record<string, unknown>, external as Record<string, unknown>) as DashboardCustomization;
}

/** Apply `byType` then `byWidgetId` patches to one widget node. Returns the
 *  original reference untouched when no patch matches. */
export function applyWidgetOverrides(
  id: string,
  widget: Record<string, unknown>,
  overrides: DashboardOverrides | undefined,
): Record<string, unknown> {
  if (!overrides || !isPlainObject(widget)) return widget;
  let out = widget;

  const type = String(widget['type'] ?? '').toLowerCase();
  const typePatch = overrides.byType && type
    ? Object.entries(overrides.byType).find(([key]) => key.toLowerCase() === type)?.[1]
    : undefined;
  if (isPlainObject(typePatch)) out = deepMergePatch(out, typePatch);

  const componentId = String(widget['componentId'] ?? '');
  const idPatch = overrides.byWidgetId
    ? overrides.byWidgetId[id] ?? (componentId ? overrides.byWidgetId[componentId] : undefined)
    : undefined;
  if (isPlainObject(idPatch)) out = deepMergePatch(out, idPatch);

  return out;
}

const SHADOW_PRESETS: Record<string, string> = {
  none: 'none',
  soft: '0 1px 1px rgba(17, 24, 39, 0.04), 0 12px 30px rgba(31, 41, 55, 0.08)',
  strong: '0 2px 4px rgba(17, 24, 39, 0.08), 0 18px 44px rgba(31, 41, 55, 0.18)',
};

/** Translate a DashboardDesign into the `--dvm-design-*` CSS variable map the
 *  published SCSS reads. Only keys the design actually sets are returned — the
 *  SCSS fallbacks (today's exact hardcoded values) cover the rest. */
export function designToCssVars(design: DashboardDesign | undefined): Record<string, string> {
  const vars: Record<string, string> = {};
  if (!design) return vars;

  const page = design.page ?? {};
  if (Array.isArray(page.gradient) && page.gradient.length >= 2) {
    const direction = page.gradientDirection || '180deg';
    const stops = page.gradient.join(', ');
    vars['--dvm-design-page-bg'] = `linear-gradient(${direction}, ${stops})`;
  } else if (page.bg) {
    vars['--dvm-design-page-bg'] = page.bg;
  }
  if (typeof page.gap === 'number') vars['--dvm-design-canvas-gap'] = `${page.gap}px`;
  if (typeof page.padding === 'number') vars['--dvm-design-canvas-padding'] = `${page.padding}px`;

  const card = design.card ?? {};
  if (card.bg) vars['--dvm-design-card-bg'] = card.bg;
  if (card.borderColor || typeof card.borderWidth === 'number') {
    const width = typeof card.borderWidth === 'number' ? card.borderWidth : 1;
    const color = card.borderColor || 'rgba(32, 44, 68, 0.1)';
    vars['--dvm-design-card-border'] = `${width}px solid ${color}`;
    // Keeps the custom border color through the tile's :hover state.
    vars['--dvm-design-card-border-color'] = color;
  }
  if (typeof card.radius === 'number') vars['--dvm-design-card-radius'] = `${card.radius}px`;
  if (card.shadow) vars['--dvm-design-card-shadow'] = SHADOW_PRESETS[card.shadow] ?? card.shadow;

  // KPI + filter-panel keys reuse the THEME token names on purpose: set on the
  // published host they sit nearer than ThemeService's <html> values, so the
  // dashboard wins over the theme with zero extra SCSS.
  const kpi = design.kpi ?? {};
  if (kpi.accentColor) vars['--dvm-stat-card-accent'] = kpi.accentColor;
  if (typeof kpi.accentWidth === 'number') vars['--dvm-stat-card-accent-width'] = `${kpi.accentWidth}px`;
  if (kpi.layout === 'stacked') vars['--dvm-stat-card-direction'] = 'column';
  else if (kpi.layout === 'inline') vars['--dvm-stat-card-direction'] = 'row';

  const filter = design.filter ?? {};
  if (filter.panelBg) vars['--dvm-filter-panel-bg'] = filter.panelBg;
  if (filter.panelBorderColor) vars['--dvm-filter-panel-border-color'] = filter.panelBorderColor;
  // Header colors reuse the EXISTING per-tile variable names (every published
  // header already reads --dvm-card-header-bg/color): setting them on the host
  // makes them the dashboard-wide default, while a tile whose Format popup set
  // its own header colors binds the same variables inline on the tile — the
  // nearer definition wins, so per-tile format beats the design automatically.
  if (card.headerBg) vars['--dvm-card-header-bg'] = card.headerBg;
  if (card.headerColor) vars['--dvm-card-header-color'] = card.headerColor;

  return vars;
}
